/**
 * Tokens visuais do jogo.
 *
 * As FONTES (familia, peso e tamanho de todo o texto da interface) tem
 * fonte unica de verdade no style.css, bloco "FONTES" dentro de :root
 * (--font-*, --fw-*, --fs-*). Este modulo le os valores do CSS em
 * runtime; fora do navegador (testes em Node) usa os fallbacks abaixo,
 * que espelham o style.css. Cores continuam definidas aqui porque o
 * canvas nao consome custom properties.
 */

const FALLBACK = {
    fontTitle: "'Silkscreen', monospace",
    fontBody: "'Handjet', monospace",
    fwNormal: '400',
    fwMedium: '500',
    fwBold: '700',
    lsNormal: 0,
    sceneTitle: 20,
    question: 28,
    tab: 20,
    tabSeparator: 13,
    footer: 12,
    timer: 22,
    instruction: 24,
    inputLabel: 18,
    gameOver: 64,
    menuPrompt: 24,
    loading: 20,
    percent: 20,
    quizOption: 20,
    quizSeparator: 18,
    modalTitle: 28,
    modalSubtitle: 22,
    modalMessage: 22,
    button: 18
};

/** Le uma custom property de :root; fora do browser devolve o fallback. */
function token (nome, fallback) {
    try {
        if (typeof document === 'undefined') return fallback;
        const valor = getComputedStyle(document.documentElement).getPropertyValue(nome).trim();
        return valor || fallback;
    } catch {
        return fallback;
    }
}

/** Idem, convertendo para numero (tokens em "px"). */
function tokenPx (nome, fallback) {
    const bruto = parseFloat(token(nome, String(fallback)));
    return Number.isFinite(bruto) ? bruto : fallback;
}

export const COLORS = {
    NEON: '#a3ff00',
    NEON_SOFT: '#d4ff70',
    MAGENTA: '#d946ef',
    PURPLE: '#a855f7',
    PURPLE_DEEP: '#2e1065',
    LILAC: '#d57fff',
    BG_DARK: '#150b28',
    BG_PANEL: '#120824',
    WHITE: '#ffffff',
    GRAY: '#b3b3b3',
    GRAY_DIM: '#6b7280',
    GRAY_LABEL: '#8c8181',
    CARD: '#e8e8e8',
    GOLD: '#facc15'
};

export const HEX = {
    NEON: 0xa3ff00,
    NEON_SOFT: 0xd4ff70,
    MAGENTA: 0xd946ef,
    PURPLE: 0xa855f7,
    PURPLE_DEEP: 0x2e1065,
    PURPLE_LINE: 0x6b21a8,
    LILAC: 0xd57fff,
    BG_DARK: 0x150b28,
    BG_PANEL: 0x120824,
    WHITE: 0xffffff,
    BLACK: 0x000000,
    GRAY: 0xb3b3b3,
    GRAY_DIM: 0x6b7280,
    GRAY_LABEL: 0x8c8181,
    CARD: 0xe8e8e8,
    CARD_STROKE: 0x671b6b
};

export const FONTS = {
    TITLE: token('--font-title', FALLBACK.fontTitle),
    BODY: token('--font-body', FALLBACK.fontBody)
};

export const WEIGHTS = {
    NORMAL: token('--fw-normal', FALLBACK.fwNormal),
    MEDIUM: token('--fw-medium', FALLBACK.fwMedium),
    BOLD: token('--fw-bold', FALLBACK.fwBold)
};

/** Espacamento entre letras (px) lido do CSS; o canvas ignora letter-spacing do CSS. */
export const LETTER_SPACING = {
    NORMAL: tokenPx('--ls-normal', FALLBACK.lsNormal)
};

/** Tamanhos base em px (numerico, para calculos como escala de botao). */
export const FONT_PX = {
    sceneTitle: tokenPx('--fs-scene-title', FALLBACK.sceneTitle),
    question: tokenPx('--fs-question', FALLBACK.question),
    tab: tokenPx('--fs-tab', FALLBACK.tab),
    tabSeparator: tokenPx('--fs-tab-separator', FALLBACK.tabSeparator),
    footer: tokenPx('--fs-footer', FALLBACK.footer),
    timer: tokenPx('--fs-timer', FALLBACK.timer),
    instruction: tokenPx('--fs-instruction', FALLBACK.instruction),
    inputLabel: tokenPx('--fs-input-label', FALLBACK.inputLabel),
    gameOver: tokenPx('--fs-game-over', FALLBACK.gameOver),
    menuPrompt: tokenPx('--fs-menu-prompt', FALLBACK.menuPrompt),
    loading: tokenPx('--fs-loading', FALLBACK.loading),
    percent: tokenPx('--fs-percent', FALLBACK.percent),
    quizOption: tokenPx('--fs-quiz-option', FALLBACK.quizOption),
    quizSeparator: tokenPx('--fs-quiz-separator', FALLBACK.quizSeparator),
    modalTitle: tokenPx('--fs-modal-title', FALLBACK.modalTitle),
    modalSubtitle: tokenPx('--fs-modal-subtitle', FALLBACK.modalSubtitle),
    modalMessage: tokenPx('--fs-modal-message', FALLBACK.modalMessage),
    button: tokenPx('--fs-button', FALLBACK.button)
};

const px = (n) => `${n}px`;

export const TEXT = {
    sceneTitle: {
        fontFamily: FONTS.TITLE,
        fontSize: px(FONT_PX.sceneTitle),
        fontStyle: WEIGHTS.BOLD,
        color: COLORS.WHITE
    },
    question: {
        fontFamily: FONTS.BODY,
        fontSize: px(FONT_PX.question),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.WHITE,
        lineSpacing: 6
    },
    tab: {
        fontFamily: FONTS.BODY,
        fontSize: px(FONT_PX.tab),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.WHITE
    },
    tabSeparator: {
        fontFamily: FONTS.BODY,
        fontSize: px(FONT_PX.tabSeparator),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.WHITE
    },
    footer: {
        fontFamily: FONTS.TITLE,
        fontSize: px(FONT_PX.footer),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.PURPLE
    },
    timer: {
        fontFamily: FONTS.TITLE,
        fontSize: px(FONT_PX.timer),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.WHITE
    },
    instruction: {
        fontFamily: FONTS.BODY,
        fontSize: px(FONT_PX.instruction),
        fontStyle: WEIGHTS.BOLD,
        color: COLORS.WHITE
    },
    inputLabel: {
        fontFamily: FONTS.BODY,
        fontSize: px(FONT_PX.inputLabel),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.WHITE
    },
    gameOverTitle: {
        fontFamily: FONTS.TITLE,
        fontSize: px(FONT_PX.gameOver),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.WHITE
    },
    menuPrompt: {
        fontFamily: FONTS.TITLE,
        fontSize: px(FONT_PX.menuPrompt),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.NEON
    },
    loadingLabel: {
        fontFamily: FONTS.TITLE,
        fontSize: px(FONT_PX.loading),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.NEON
    },
    loadingPercent: {
        fontFamily: FONTS.BODY,
        fontSize: px(FONT_PX.percent),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.WHITE
    },
    quizOption: {
        fontFamily: FONTS.BODY,
        fontSize: px(FONT_PX.quizOption),
        fontStyle: WEIGHTS.BOLD,
        color: COLORS.BG_DARK,

    },
    quizSeparator: {
        fontFamily: FONTS.BODY,
        fontSize: px(FONT_PX.quizSeparator),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.GRAY_DIM
    },
    modalTitle: {
        fontFamily: FONTS.TITLE,
        fontSize: px(FONT_PX.modalTitle),
        fontStyle: WEIGHTS.MEDIUM,
        color: COLORS.NEON,
        align: 'center'
    },
    modalSubtitle: {
        fontFamily: FONTS.BODY,
        fontSize: px(FONT_PX.modalSubtitle),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.WHITE
    },
    modalMessage: {
        fontFamily: FONTS.BODY,
        fontSize: px(FONT_PX.modalMessage),
        fontStyle: WEIGHTS.NORMAL,
        color: COLORS.WHITE,
        align: 'center',
        lineSpacing: 6
    }
};

// Aplica o espacamento entre letras padrao a todos os estilos que nao
// definirem o proprio valor (o Phaser nao le letter-spacing do CSS).
for (const estilo of Object.values(TEXT)) {
    if (estilo.letterSpacing === undefined) {
        estilo.letterSpacing = LETTER_SPACING.NORMAL;
    }
}

export const FRAME = {
    MODAL: { cornerSize: 14, gap: 6, outerWidth: 3, innerWidth: 2 },
    CARD: { cornerSize: 10, gap: 4, outerWidth: 2, innerWidth: 1.5 }
};


/* ------------------------------------------------------------------ *
 * Tema Figma (grafite + lima) — `DP-TF-002`.
 * Usado pelas telas novas (Login, Aguarde e seguintes). As cenas ainda
 * não migradas continuam nos tokens acima até a entrega de cada fase.
 * ------------------------------------------------------------------ */

export const ARCADE = {
    LIMA: '#d1e863',
    VERDE: '#68a154',
    GRAFITE: '#1f1f1f',
    CAMPO: '#282b19',
    BRANCO: '#ffffff',
    TEXTO_APAGADO: 'rgba(255,255,255,0.7)',
    ERRO: '#ff7a6b',
    TINTA: '#1a1a1a',
    TINTA_APAGADA: 'rgba(26,26,26,0.55)'
};

export const ARCADE_HEX = {
    LIMA: 0xd1e863,
    VERDE: 0x68a154,
    GRAFITE: 0x1f1f1f,
    CAMPO: 0x282b19,
    BRANCO: 0xffffff,
    FIO_CAMPO: 0x525252,
    BOTAO_OFF: 0x7a7a7a,
    FIO_OFF: 0xacacac,
    TINTA: 0x1a1a1a,
    VERMELHO: 0xd32f2f
};

export const ARCADE_FONTS = {
    TITULO: "'Silkscreen', monospace",
    TEXTO: "'Barlow Semi Condensed', 'Arial Narrow', sans-serif"
};

/** Silkscreen no Figma usa tracking de -10% do corpo. */
const pixel = (tamanho, cor = ARCADE.BRANCO) => ({
    fontFamily: ARCADE_FONTS.TITULO,
    fontSize: `${tamanho}px`,
    fontStyle: '700',
    color: cor,
    letterSpacing: -tamanho / 10
});

const corrido = (tamanho, peso = '500', cor = ARCADE.BRANCO) => ({
    fontFamily: ARCADE_FONTS.TEXTO,
    fontSize: `${tamanho}px`,
    fontStyle: peso,
    color: cor
});

export const ARCADE_TEXT = {
    tituloGigante: pixel(128),
    botao: pixel(24),
    progresso: pixel(26.6, ARCADE.LIMA),
    atalho: pixel(16, ARCADE.LIMA),
    instrucao: { ...corrido(24), align: 'center' },
    subtitulo: { ...corrido(32), align: 'center' },
    erro: { ...corrido(20, '500', ARCADE.ERRO), align: 'center' },
    rodape: corrido(16, '600', ARCADE.TINTA),

    // Páginas de fundo lima (Materiais, Mural, Ranking)
    tituloPagina: {
        ...pixel(64),
        shadow: { offsetX: 4, offsetY: 4, color: ARCADE.VERDE, blur: 0, fill: true }
    },
    jogador: { ...pixel(16), fontStyle: '400' },
    voltar: pixel(20, ARCADE.TINTA),
    introducao: corrido(28, '500', ARCADE.TINTA),
    rotuloMaterial: { ...pixel(16, ARCADE.TINTA), fontStyle: '400', align: 'center', lineSpacing: 4 },
    nota: { ...corrido(18, '500', ARCADE.TINTA), align: 'center' },
    dataNota: { ...pixel(18, ARCADE.TINTA), fontStyle: '400' },
    adicionarNota: { ...pixel(16, ARCADE.TINTA), fontStyle: '400', align: 'center', lineSpacing: 6 },
    tituloPopup: pixel(32, ARCADE.TINTA),
    textoPopup: { ...corrido(28, '500', ARCADE.TINTA), align: 'center' },
    nomeRanking: pixel(24, ARCADE.TINTA),
    pontosRanking: corrido(24, '800', ARCADE.TINTA),
    aviso: { ...corrido(24, '500', ARCADE.TINTA), align: 'center' },

    // Fases (Scan Pixel e seguintes)
    // Peso normal (Silkscreen Regular): no negrito, o "4" da fonte pixel parecia um "9" ("ENIGMA 4/5").
    tituloFase: { ...pixel(30, ARCADE.TINTA), fontStyle: '400' },
    aba: pixel(24),
    instrucaoFase: { ...corrido(28, '500', ARCADE.TINTA), align: 'center' },
    codigo: { ...pixel(22), fontStyle: '400', letterSpacing: 0 },
    rotuloCodigo: pixel(18, ARCADE.TINTA),
    tituloModal: {
        ...pixel(52),
        shadow: { offsetX: 3, offsetY: 3, color: ARCADE.VERDE, blur: 0, fill: true }
    },
    textoModal: { ...corrido(30, '500'), align: 'center', lineSpacing: -1 },
    pixelModal: { ...pixel(22), align: 'center', lineSpacing: 6, letterSpacing: -0.9 },
    manchete: { ...corrido(30, '700', ARCADE.TINTA), lineSpacing: 2 },
    placar: { ...pixel(26.6, ARCADE.TINTA), fontStyle: '400' },

    // Laboratório de Pixels
    cronometro: pixel(28, ARCADE.TINTA),
    rotuloPergunta: corrido(28, '700', ARCADE.TINTA),
    letraOpcao: pixel(16, ARCADE.TINTA),
    opcao: { ...corrido(18, '500', ARCADE.TINTA), lineSpacing: 0 },
    gigante: { ...pixel(88), letterSpacing: 4 }
};

/** Moldura pixel de três fios do Figma (cada fio com 2,714 px). */
export const MOLDURA = { FIO: 2.714, ALTURA: 70, CHANFRO: 10 };
